import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { UPDATE_LOAN_CATEGORY } from "apollo/mutuations";
import { ADMIN_LOAN_CATEGORIES, LOAN_PROVIDERS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect } from "react";

const LoanCategoryEditModal = ({ setOpenedEdit, editData }) => {
  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      icon: "",
      sort_order: 0,
      provider_id: "",
      status: "ACTIVE",
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      sort_order: (value) =>
        value >= 0 ? null : "Sort order must be 0 or greater",
    },
  });

  // Fetch loan providers
  const { data: providersData } = useQuery(LOAN_PROVIDERS, {
    variables: {
      first: 1000,
      page: 1,
      search: "",
    },
  });

  const providerOptions =
    providersData?.loanProviders?.data?.map((provider) => ({
      value: provider.id,
      label: provider.name,
    })) || [];

  useEffect(() => {
    if (editData) {
      form.setValues({
        name: editData.name || "",
        description: editData.description || "",
        icon: editData.icon || "",
        sort_order: editData.sort_order || 0,
        provider_id: editData.provider_id || "",
        status: editData.status === "ACTIVE" ? 1 : 0 || 1,
      });
    }
  }, [editData]);

  const [updateLoanCategory, { loading }] = useMutation(UPDATE_LOAN_CATEGORY, {
    refetchQueries: [
      {
        query: ADMIN_LOAN_CATEGORIES,
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Category Updated Successfully",
      });
      setOpenedEdit(false);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Update Loan Category",
      });
    },
  });

  const handleSubmit = (values) => {
    updateLoanCategory({
      variables: {
        id: editData.id,
        name: values.name,
        description: values.description || null,
        icon: values.icon || null,
        sort_order: values.sort_order || 0,
        provider_id: values.provider_id || null,
        status: values.status,
      },
    });
  };

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  return (
    <div style={{ position: "relative", minHeight: "400px" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="Name"
          placeholder="Enter category name"
          required
          {...form.getInputProps("name")}
          mb="md"
        />

        <Textarea
          label="Description"
          placeholder="Enter description"
          minRows={3}
          {...form.getInputProps("description")}
          mb="md"
        />

        <TextInput
          label="Icon"
          placeholder="las la-store"
          {...form.getInputProps("icon")}
          mb="md"
          description="Enter Line Awesome icon class (e.g., 'las la-store')"
        />

        <NumberInput
          label="Sort Order"
          placeholder="0"
          min={0}
          {...form.getInputProps("sort_order")}
          mb="md"
        />

        <Select
          label="Loan Provider"
          placeholder="Select provider"
          data={providerOptions}
          searchable
          clearable
          {...form.getInputProps("provider_id")}
          mb="md"
        />

        <Select
          label="Status"
          placeholder="Select status"
          data={statusOptions}
          {...form.getInputProps("status")}
          mb="md"
        />

        <Group position="right" mt="xl">
          <Button
            type="submit"
            leftIcon={<IconDeviceFloppy size={16} />}
            style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
            disabled={loading}
          >
            Update
          </Button>
        </Group>
      </form>
    </div>
  );
};

export default LoanCategoryEditModal;
